import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useSelector, useDispatch } from "react-redux";
import { selectAdminToken } from "../../sessionSlice";
import { fetchThemeData } from "../../themeSlice";
import "./QuestionsTable.css";
import { useNavigate } from "react-router-dom";
import { updateThemeData } from "../../functions/updateThemeData";

const VALID_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const MAX_FILE_BYTES = 2 * 1024 * 1024;

async function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

const QuestionsTable = () => {
  const dispatch = useDispatch();
  const token = useSelector(selectAdminToken);
  const { currentTheme, data: themeData } = useSelector((state) => state.theme);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(-1);
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentTheme || !token) return;
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme, token]);

  const existingImages = useMemo(() => {
    const arr = themeData?.puzzle_images;
    return Array.isArray(arr) ? arr : [];
  }, [themeData?.puzzle_images]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!VALID_TYPES.includes(file.type)) {
      Swal.fire("Invalid file", "Only JPG/JPEG/PNG images are allowed.", "error");
      event.target.value = "";
      return;
    }
    if (file.size >= MAX_FILE_BYTES) {
      Swal.fire("Invalid file", "Image size must be below 2MB.", "error");
      event.target.value = "";
      return;
    }

    try {
      const { width, height } = await getImageDimensions(file);
      if (width !== 550 || height !== 550) {
        Swal.fire("Invalid dimensions", "Image must be exactly 550x550.", "error");
        event.target.value = "";
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    } catch {
      Swal.fire("Error", "Unable to read image dimensions.", "error");
      event.target.value = "";
    }
  };

  const handleUploadImage = async () => {
    if (!selectedFile) {
      Swal.fire("Select image", "Please choose one image first.", "info");
      return;
    }
    if (!currentTheme || !token || !themeData) {
      Swal.fire("Error", "Theme data is not ready yet.", "error");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("themeName", currentTheme);
      formData.append("puzzleImage", selectedFile);
      formData.append(
        "extraData",
        JSON.stringify({
          mode: "puzzle_image_upload",
        })
      );

      const uploadResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/updateThemeMedia`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!uploadResp.ok) {
        const err = await uploadResp.json().catch(() => ({}));
        throw new Error(err.error || "Failed to upload image to S3");
      }

      const uploadData = await uploadResp.json();
      const uploadedFilename = uploadData?.uploadedFiles?.puzzleImage;
      if (!uploadedFilename) {
        throw new Error("Upload response missing filename");
      }

      const nextImages = [...existingImages, uploadedFilename];
      await updateThemeData({
        token,
        payload: {
          currentTheme,
          data: {
            puzzle_images: nextImages,
          },
        },
      });

      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      setSelectedFile(null);
      setPreviewUrl("");
      Swal.fire("Uploaded", "Image uploaded successfully.", "success");
    } catch (error) {
      Swal.fire("Error", error?.message || "Image upload failed.", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (index) => {
    if (!currentTheme || !token || !themeData) return;
    const imageName = existingImages[index];
    if (!imageName) return;

    const confirm = await Swal.fire({
      title: "Delete image?",
      text: "This will remove the image from puzzle images.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });
    if (!confirm.isConfirmed) return;

    setDeletingIndex(index);
    try {
      const nextImages = existingImages.filter((_, idx) => idx !== index);
      await updateThemeData({
        token,
        payload: {
          currentTheme,
          data: {
            puzzle_images: nextImages,
          },
        },
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Deleted", "Image removed successfully.", "success");
    } catch (error) {
      Swal.fire("Error", error?.message || "Failed to delete image.", "error");
    } finally {
      setDeletingIndex(-1);
    }
  };

  return (
    <>
      <div className="back-button-holder">
        <button
          className="back-button"
          onClick={() => navigate("/admin/rules")}
        >
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>
      <div className="question-page-blue-holder">
        <div className="blue-tabs-container">
          <div className="blue-tabs"></div>
          <div className="blue-tabs blue-tabs-active"></div>
          <div className="blue-tabs"></div>
        </div>
      </div>

      <div className="questions-table-container">
        <div className="question-header">
          <div className="question-header-title">Puzzle Images</div>
          <div className="question-header-subtitle">Manage puzzle image library</div>
        </div>

        <div className="image-upload-panel">
          <p className="image-upload-note">
            Upload one image at a time. Allowed: JPG/PNG, exact 550x550, size below 2MB.
          </p>
          <div className="image-upload-row">
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleFileChange}
              disabled={uploading}
            />
            <button
              className="btn outline"
              type="button"
              onClick={handleUploadImage}
              disabled={uploading || !selectedFile}
            >
              {uploading ? "Uploading..." : "Upload Image"}
            </button>
          </div>

          {previewUrl ? (
            <div className="image-preview-box">
              <img src={previewUrl} alt="Selected preview" />
            </div>
          ) : null}
        </div>

        <div className="uploaded-images-grid">
          {existingImages.length === 0 ? (
            <div className="no-data">No uploaded images yet.</div>
          ) : (
            existingImages.map((name, idx) => (
              <div className="uploaded-image-item" key={`${name}-${idx}`}>
                <button
                  type="button"
                  className="uploaded-image-delete-btn"
                  onClick={() => handleDeleteImage(idx)}
                  disabled={deletingIndex === idx || uploading}
                  aria-label={`Delete image ${idx + 1}`}
                >
                  ×
                </button>
                <img src={`${process.env.REACT_APP_S3_PATH}${name}`} alt={`Puzzle ${idx + 1}`} />
              </div>
            ))
          )}
        </div>

      </div>
    </>
  );
};

export default QuestionsTable;